import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  COMMAND_PRIORITY_CRITICAL,
  CUT_COMMAND,
  DELETE_CHARACTER_COMMAND,
  DELETE_LINE_COMMAND,
  DELETE_WORD_COMMAND,
  DROP_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  INSERT_LINE_BREAK_COMMAND,
  INSERT_PARAGRAPH_COMMAND,
  INSERT_TAB_COMMAND,
  KEY_DOWN_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  PASTE_COMMAND,
  CONTROLLED_TEXT_INSERTION_COMMAND,
  REMOVE_TEXT_COMMAND,
} from 'lexical';
import { useImpressionEditorContext } from '../../Context';

const SELECTION_KEYS = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'Shift',
  'Control',
  'Meta',
  'Alt',
  'Escape',
  'Tab',
]);

/**
 * Blocks all content-modifying input when the editor is in
 * annotate-only mode (canAnnotate && !isEditing). Text selection
 * and navigation keys are still allowed so users can highlight
 * text for annotations.
 */
export const InputGuardPlugin = () => {
  const { isEditing, canAnnotate } = useImpressionEditorContext();
  const [editor] = useLexicalComposerContext();

  const shouldGuard = canAnnotate && !isEditing;

  useEffect(() => {
    if (!shouldGuard) return;

    const disposers = [
      editor.registerCommand(
        KEY_DOWN_COMMAND,
        (event: KeyboardEvent) => {
          if (SELECTION_KEYS.has(event.key)) return false;
          // Allow Ctrl/Cmd+C (copy) and Ctrl/Cmd+A (select all)
          if (
            (event.metaKey || event.ctrlKey) &&
            (event.key === 'c' || event.key === 'a')
          ) {
            return false;
          }
          event.preventDefault();
          return true;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        PASTE_COMMAND,
        () => true,
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        CUT_COMMAND,
        () => true,
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        DROP_COMMAND,
        () => true,
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        CONTROLLED_TEXT_INSERTION_COMMAND,
        () => true,
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        DELETE_CHARACTER_COMMAND,
        () => true,
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        DELETE_WORD_COMMAND,
        () => true,
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        DELETE_LINE_COMMAND,
        () => true,
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        INSERT_LINE_BREAK_COMMAND,
        () => true,
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        INSERT_PARAGRAPH_COMMAND,
        () => true,
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        REMOVE_TEXT_COMMAND,
        () => true,
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        FORMAT_TEXT_COMMAND,
        () => true,
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        FORMAT_ELEMENT_COMMAND,
        () => true,
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        INSERT_TAB_COMMAND,
        () => true,
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        INDENT_CONTENT_COMMAND,
        () => true,
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        OUTDENT_CONTENT_COMMAND,
        () => true,
        COMMAND_PRIORITY_CRITICAL,
      ),
    ];

    const rootElement = editor.getRootElement();
    const handleBeforeInput = (e: Event) => e.preventDefault();
    rootElement?.addEventListener('beforeinput', handleBeforeInput);

    return () => {
      disposers.forEach(dispose => dispose());
      rootElement?.removeEventListener('beforeinput', handleBeforeInput);
    };
  }, [editor, shouldGuard]);

  return null;
};
